<template>
  <div class="flex flex-col gap-5">
    <GridSkeleton v-if="loading"/>
    <template v-else-if="displayedList.length > 0 || filterText">
      <InputGroup type="text" :placeholder="$t('message.search.placeholder')"
                  prependIcon="heroicons-outline:search" merged
                  @update:modelValue="searchInOrders"/>
      <div class="grid lg:grid-cols-4 md:grid-cols-3 sm:grid-cols-2 grid-cols-1 gap-5 pt-5">
        <div v-for="(order, index) in displayedList" :key="index" @click="showTakeOrderModal(order)"
             class="bg-white dark:bg-slate-800 p-4 relative z-[1] rounded-2xl text-white flex flex-col justify-between overflow-hidden cursor-pointer gap-4"
        >
          <div class="w-full">
            <div class="dir-ltr mb-1 flex flex-row justify-between items-start">
              <span class="dir-ltr text-left text-[15px] text-slate-900 dark:text-white mb-2 me-2">
                {{ order.game }}
              </span>
            </div>
            <p class="dir-ltr text-[13px] font-medium text-slate-900 dark:text-white text-opacity-80 break-words">
              {{ order.title }}
            </p>
          </div>
          <div class="flex flex-col gap-4 relative">
            <div class="flex flex-row justify-between">
              <p class="text-success text-[12px] font-weight-bolder text-lime-600">
                {{ getFormattedDate(order.creationTime, {jalali: 'jD jMMMM', gregorian: 'D MMMM'}) }}
              </p>
              <h6 class="text-[13px]">
                {{ locale === 'fa' ? order.price + ' دلار' : '$' + order.price }}
              </h6>
            </div>
          </div>
          <div class="absolute right-0 top-0" v-if="order.userApplied">
            <Tooltip
                placement="top"
                arrow
            >
              <template v-slot:button>
              <span
                  class="absolute right-0 top-0 text-xl text-info-600 px-2 py-2 rounded-full inline-block uppercase h-auto opacity-60">
            <Icon icon="mdi:progress-tick"></Icon>
          </span>
              </template>
              <template v-slot:default>
                {{ $t('message.you reserved this order') }}
              </template>
            </Tooltip>
          </div>
          <img v-if="themeSettingsStore.isDark"
               src="@/assets/images/svg/line.svg"
               alt=""
               class="absolute left-0 bottom-0 w-full z-[-1] opacity-50"
          />
          <img v-else
               src="@/assets/images/svg/line-dark.svg"
               alt=""
               class="absolute left-0 bottom-0 w-full z-[-1] opacity-50"
          />
        </div>
      </div>
      <Pagination v-if="orders.length / 10 > 1"
                  wrapperClass="bg-slate-100 dark:bg-slate-500 mt-5 w-fit py-2 px-3 rounded mx-auto sticky z-[2] bottom-5"
                  :current="thisPage"
                  :total="orders.length"
                  :per-page="10"
                  @page-changed="doPaginate"
      />
      <Modal
          :title="$t('message.details')"
          :hasButton="false"
          labelClass="bg-black-100 dark:bg-slate-900 dark:text-slate-50 btn-sm"
          ref="modal2"
          :activeModal="isShowRemoveModal"
          @closeModal="isShowRemoveModal = false"
          centered
      >
        <form ref="form">
          <div
              class="flex flex-col gap-6 price-table text-slate-900 dark:text-white relative overflow-hidden z-[1]"
          >
            <div
                class="overlay absolute right-0 top-0 w-full h-full z-[-1]"
            >
              <img src="@/assets/images/all-img/big-shap3.png" alt="" class="ml-auto block"/>
            </div>
            <header>
              <h4
                  class="text-xl mb-2 text-slate-900 dark:text-slate-300"
              >
                {{ orderDetails.game }}
              </h4>
              <div
                  class="space-x-4 relative flex items-center mb-2 text-slate-900 dark:text-slate-300" dir="ltr"
              >
                <span class="text-[32px] leading-10 font-medium">${{ orderDetails.price }}</span>
                <span
                    class="text-xs bg-warning-50 text-warning-500 font-medium px-2 py-1 rounded-full inline-block dark:bg-slate-700 uppercase h-auto"
                >{{ orderDetails.state }}</span>
              </div>
              <p
                  class="text-sm text-slate-500 dark:text-slate-300"
              >
                {{ orderDetails.title }}
              </p>
            </header>
            <div class="price-body space-y-8">
              <p
                  class="text-sm leading-5 text-slate-600 dark:text-slate-300"
                  v-html="orderDetails.description">
              </p>
            </div>
            <Alert v-if="orderDetails.userApplied"
                   type="info-outline"
                   icon="heroicons-outline:information-circle"
            >
              <p v-if="locale === 'fa'">
                شما این سفارش را <span class="font-semibold">رزرو </span>کرده اید.
                برای لغو رزرو روی دکمه کنسل کلیک کنید
              </p>
              <p v-else>
                You <span class="font-semibold">reserved</span> this order. If you want to cancel it, click on
                CANCEL button
              </p>
            </Alert>
          </div>
        </form>
        <template v-slot:footer>
          <Button
              :text="$t('message.close')"
              btnClass="btn-outline-dark "
              @click="()=>{$refs.modal2.closeModal();}"
          />
          <button type="submit" @click="takeOrder"
                  class="btn text-center flex items-center justify-center gap-3"
                  :class="orderDetails.userApplied ? 'btn-outline-danger' : 'btn-outline-success'">
            {{ orderDetails.userApplied ? $t('message.cancel') : $t('message.reserve') }}
            <Icon v-show="loading_reserve_or_cancel" icon="eos-icons:bubble-loading" height="24"/>
          </button>
        </template>
      </Modal>
    </template>
    <div v-else
         class="flex justify-center flex-wrap items-center flex-col text-center gap-10"
    >
      <img src="@/assets/images/all-img/no-data1.png" alt=""/>

      <p class="font-normal text-base text-slate-500 dark:text-slate-300">
        {{ $t('message.there is no order to show') }}
      </p>
    </div>
    <Button
        icon="mingcute:game-2-line"
        :text="$t('message.change_games')"
        btnClass="dark:bg-primary-500 dark:bg-opacity-25 dark:hover:bg-blue-900 text-white bg-primary-400 hover:bg-blue-900 rounded-xl w-max text-white sticky bottom-[20px] z-[999]"
        iconPosition="right"
        link="/games/account"
    />
  </div>
</template>

<script>
import {useOrdersStore} from "@/stores/orders";
import {useUserStore} from "@/stores/user";
import {useThemeSettingsStore} from "@/stores/themeSettings";
import {ref} from "vue";
import {useRoute} from "vue-router";
import GridSkeleton from "@/components/Skeleton/Orders.vue";
import Pagination from "@/components/Pagination/index.vue";
import InputGroup from "@/components/InputGroup/index.vue";
import router from "@/router";
import {computed} from "vue";
import Button from "@/components/Button/index.vue";
import {Icon} from "@iconify/vue";
import Modal from "@/components/Modal/Modal.vue";
import {useI18n} from "vue-i18n";
import jalaliMoment from 'jalali-moment';
import Tooltip from "@/components/Tooltip/index.vue";
import Alert from '@/components/Alert/index.vue';
import {useFormattedDate} from '@/composables/useFormattedDate';

export default {
  name: "accountOrders",
  components: {
    Tooltip,
    GridSkeleton,
    Pagination,
    InputGroup,
    Button,
    Icon,
    Modal,
    Alert
  },
  setup() {
    const ordersStore = useOrdersStore();
    const userStore = useUserStore();
    const themeSettingsStore = useThemeSettingsStore()
    const route = useRoute();
    //const router = useRouter();
    const loading = ref(true)
    const loading_reserve_or_cancel = ref(false)
    const reserve_or_cancel_result = ref(false)
    const orders = ref([])
    const originalOrders = ref([])
    const thisPage = ref(route.query.page !== undefined ? Number(route.query.page) : 1);
    const perPage = 10;
    const thisRoute = route.name;
    const filterText = ref(null);
    const isShowRemoveModal = ref(false);
    const orderDetails = ref(null);
    const {locale} = useI18n({useScope: 'global'})
    const {getFormattedDate} = useFormattedDate();

    async function getAccountOrders() {
      loading.value = true;
      await ordersStore.action_get_account_active_hot_orders()
      const gamesToFilter = userStore.getAccountGamePreferences;
      if (ordersStore.getAccountOrders.length) {
        orders.value = ordersStore.getAccountOrders.filter(item => {
          return gamesToFilter.includes(item.game);
        });
      }
      originalOrders.value = orders.value
      loading.value = false
    }

    function searchInOrders(emittedValue) {
      filterText.value = emittedValue;

      const filteredData = originalOrders.value.filter(item =>
          Object.values(item).some(value =>
              String(value).toLowerCase().includes(String(emittedValue).toLowerCase())
          )
      );

      if (emittedValue) {
        orders.value = filteredData;
      } else {
        orders.value = originalOrders.value;
      }
    }


    function paginate(list) {
      let from = (thisPage.value * perPage) - perPage;
      let to = (thisPage.value * perPage);
      return list.slice(from, to);
    }

    function doPaginate(pageNumber) {
      thisPage.value = pageNumber;
      router.push({name: thisRoute, query: {page: pageNumber}});
    }

    async function takeOrder() {
      loading_reserve_or_cancel.value = true;
      if (orderDetails.value.userApplied) {
        reserve_or_cancel_result.value = await ordersStore.action_removeAccountHotOrderApplicant({id: orderDetails.value.id})
        loading_reserve_or_cancel.value = false;
        if (reserve_or_cancel_result.value) {
          orderDetails.value.userApplied = false
        }
      } else {
        reserve_or_cancel_result.value = await ordersStore.action_addAccountHotOrderApplicant({id: orderDetails.value.id})
        loading_reserve_or_cancel.value = false;
        if (reserve_or_cancel_result) {
          orderDetails.value.userApplied = true
        }
      }
    }

    function showTakeOrderModal(order) {
      isShowRemoveModal.value = true
      orderDetails.value = order;
      orderDetails.value.description = order.description.replace(/\n/g, '<br>')
    }

    const displayedList = computed(() => paginate(orders.value));

    getAccountOrders()


    return {
      loading,
      loading_reserve_or_cancel,
      orders,
      displayedList,
      thisPage,
      perPage,
      doPaginate,
      filterText,
      searchInOrders,
      themeSettingsStore,
      isShowRemoveModal,
      showTakeOrderModal,
      takeOrder,
      orderDetails,
      locale,
      getFormattedDate
    }
  }
}
</script>

<style scoped>
.dir-ltr {
  direction: ltr;
}

.dir-rtl {
  direction: rtl;
}
</style>
